import {
  Box,
  Card,
  CardActionArea,
  CardMedia,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid2,
  Radio,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { getOptionValue } from '@lib/form';
import { StandaloneFieldLabel } from '../../FieldLabel';
import { FIELD_BORDER_COLOR } from '../../formTheme';
import getFieldStatus from '../../fieldStatus';

/**
 * Option cards.
 *
 * Selection used to be drawn with `elevation={20}` against `elevation={1}`, on top of a 2px
 * primary border that every card carried whether it was selected or not — so the only real
 * signal was a shadow jump, and the border said "selected" about all of them at once. Now
 * the border and the label weight carry it and nothing moves.
 */
export default function OptionList(props) {
  const {
    name,
    disabled,
    value,
    label,
    isRequired,
    error,
    touched,
    isMultiple,
    options,
    handleChange,
    setFieldTouched,
  } = props;
  // No `helperText` prop on this variant, so `message` is the error or nothing.
  const { hasError, message } = getFieldStatus({ error, touched });

  let columnSpan = 6;
  if (options.length > 2 && options.length < 4) columnSpan = 4;
  else if (options.length >= 4) columnSpan = 3;

  const handleToggleItem = (newValue) => () => {
    if (disabled) {
      return;
    }

    setFieldTouched(name);

    if (isMultiple) {
      const fieldValues = value ?? [];

      if (fieldValues.includes(newValue)) {
        handleChange(fieldValues.filter((v) => v !== newValue));
      } else {
        handleChange([...fieldValues, newValue]);
      }
    } else {
      handleChange(newValue);
    }
  };

  return (
    <Box>
      <StandaloneFieldLabel id={`${name}-label`} isRequired={isRequired} label={label} />
      <Grid2
        aria-labelledby={label ? `${name}-label` : undefined}
        container
        role="group"
        spacing={1.5}
      >
        {options.map((item) => {
          const itemValue = getOptionValue(item);
          const image = item.image?.data?.attributes?.formats?.medium?.url
                ?? item.image?.data?.attributes?.url;
          const isSelected = isMultiple
            ? (value ?? []).includes(itemValue)
            : value === itemValue;

          if (!image) {
            return (
              // `columnSpan` used to apply at every breakpoint, so four options were four
              // 25%-wide columns on a 390px screen and the labels ran into each other.
              <Grid2 key={item.id} size={{ xs: 12, sm: 6, md: columnSpan }}>
                <FormControlLabel
                  control={isMultiple ? (
                    <Checkbox
                      checked={isSelected}
                      onChange={handleToggleItem(itemValue)}
                    />
                  ) : (
                    <Radio
                      checked={isSelected}
                      color="primary"
                      onChange={handleToggleItem(itemValue)}
                    />
                  )}
                  label={item.label}
                />
              </Grid2>
            );
          }

          return (
            <Grid2 key={item.id} size={{ xs: 6, md: columnSpan }}>
              <Card
                aria-pressed={isSelected}
                sx={{
                  height: '100%',
                  // Held at 2px in both states so selecting a card cannot nudge the grid.
                  borderWidth: 2,
                  borderColor: isSelected ? 'primary.main' : FIELD_BORDER_COLOR,
                }}
                variant="outlined"
              >
                <CardActionArea
                  disabled={disabled}
                  onClick={handleToggleItem(itemValue)}
                  sx={{
                    height: '100%',
                    p: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <CardMedia
                    alt=""
                    component="img"
                    image={image}
                    sx={{ height: 40, objectFit: 'contain' }}
                  />
                  <Typography
                    align="center"
                    sx={{ fontWeight: isSelected ? 700 : 600 }}
                    variant="body2"
                  >
                    {item.label}
                  </Typography>
                  {item.badge && (
                    <Typography color="text.secondary" variant="caption">
                      {item.badge}
                    </Typography>
                  )}
                </CardActionArea>
              </Card>
            </Grid2>
          );
        })}
      </Grid2>
      {hasError && (
        <FormHelperText error>
          {message}
        </FormHelperText>
      )}
    </Box>
  );
}

OptionList.propTypes = {
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  error: PropTypes.string,
  // Formik reports an array-valued field's touched state as an array of booleans,
  // not a boolean.
  touched: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  isMultiple: PropTypes.bool,
  options: PropTypes.array.isRequired,
  handleChange: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
};
